/* DNAcademy lesson block renderer + quiz runner.
   window.DNA.blocks.renderLesson(container, data, ctx)
     data = { kind, content, block_state, quiz, youtube_id }   (Worker lesson payload)
     ctx  = { course, lesson, api, onProgress }
   Rich-text fields (text.body, text.bodyRight, image.caption? no — caption is plain; accordion/tabs/process
   item bodies, image.asideBody, audio.transcript, flashcard front/back, marker body, knowledgeCheck question,
   quiz question, feedback html; slice 5: statement body, quote item quote, list item body, timeline event
   body, buttons item description) were sanitised at publish time and are the ONLY strings
   that go through innerHTML. Everything else is textContent. No external libraries. */
(() => {
  // ---------- tiny DOM helpers ----------
  const el = (tag, cls, text) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = String(text);
    return n;
  };
  const rich = (tag, cls, htmlStr) => {
    const n = el(tag, cls);
    n.innerHTML = typeof htmlStr === 'string' ? htmlStr : '';
    return n;
  };
  const reducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollToNode = (node) => {
    if (!node || typeof node.scrollIntoView !== 'function') return;
    node.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
  };
  // Move keyboard/AT focus to newly shown content, then (optionally) scroll it into view.
  const focusThenScroll = (node, scroll) => {
    if (!node) return;
    if (!node.hasAttribute('tabindex')) node.tabIndex = -1;
    try { node.focus({ preventScroll: true }); } catch (_) { node.focus(); }
    if (scroll !== false) scrollToNode(node);
  };
  const img = (file, cls) => {
    if (!file || !file.url) return null;
    const i = el('img', cls);
    i.src = file.url;
    i.alt = file.alt || '';
    if (file.width) i.width = file.width;
    if (file.height) i.height = file.height;
    i.loading = 'lazy';
    return i;
  };
  let uid = 0;
  const nextId = (prefix) => prefix + '-' + (++uid);
  const shuffle = (arr) => {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  // ---------- API helpers ----------
  const postJson = async (ctx, path, body) => {
    const res = await ctx.api(path, { method: 'POST', body: JSON.stringify(body) });
    let json = null;
    try { json = await res.json(); } catch (_) { json = null; }
    return { ok: !!res.ok, status: res.status, json };
  };
  const lessonPath = (ctx, suffix) =>
    '/api/courses/' + encodeURIComponent(ctx.course) + '/lessons/' + encodeURIComponent(ctx.lesson) + suffix;
  const postBlockState = (ctx, blockState) =>
    postJson(ctx, '/api/progress', {
      course_handle: ctx.course,
      lesson_handle: ctx.lesson,
      block_state: blockState
    }).catch(() => ({ ok: false, status: 0, json: null }));

  // ---------- "the student actually read it" ----------
  /* Run `done` once the END of the lesson has been on screen AND the student
     has been with the lesson for a few seconds. Both halves matter: a lesson
     shorter than the viewport is at its end from the first frame, so the time
     is what stops it completing on open (which is all the player used to ask
     for). Returns a stop function; the watch also gives up by itself if the
     lesson is replaced before the student got there. */
  const READ_DWELL_MS = 5000;
  /* `alive` is an element from THIS render: the classroom reuses the container
     between lessons, so a live container proves nothing — a detached segment
     proves the student moved on. Nothing is added to the DOM (the container is
     a grid; an extra child would take a row and a gap), and the end is read
     from the container's own box rather than watched with an
     IntersectionObserver, which does not report a zero-height sentinel. */
  const whenReadToEnd = (container, alive, done) => {
    let fired = false, atEnd = false, waited = false;
    const stop = () => {
      window.removeEventListener('scroll', check, true);
      window.removeEventListener('resize', check);
      clearInterval(timer);
    };
    const maybe = () => {
      if (fired || !atEnd || !waited) return;
      fired = true;
      stop();
      if (alive.isConnected === false) return;  // another lesson is on screen now
      done();
    };
    function check() {
      if (alive.isConnected === false) { stop(); return; }  // another lesson is on screen now
      if (atEnd) return;
      const box = container.getBoundingClientRect();
      const view = window.innerHeight || document.documentElement.clientHeight || 0;
      if (box.bottom <= view + 4) { atEnd = true; maybe(); }
    }
    // Images, embeds and fonts all push the end of the lesson further down
    // after first paint, so the end is re-read on a timer as well as on scroll.
    const timer = setInterval(check, 1000);
    setTimeout(() => { waited = true; maybe(); }, READ_DWELL_MS);
    window.addEventListener('scroll', check, true);
    window.addEventListener('resize', check);
    check();
    return stop;
  };

  // ---------- shared block settings (Studio: Style + Format) ----------
  /* Any block may carry `settings`: a background, padding above and below, and
     (text blocks) a narrower width. Every field is nullable and null means
     "exactly what the player drew before settings existed", so a lesson
     published before them gets no class, no inline style, nothing: it renders
     pixel-identically. The decoration is a pure function of the settings;
     Studio's canvas runs an identical copy (admin src/lib/blockSettings.ts) and
     one shared test table holds the two to the same output. */
  const SETTINGS_BG_TOKENS = ['raised', 'tint', 'accent', 'contrast'];
  const HEX_COLOUR = /^#[0-9a-f]{6}$/i;
  // Absolute http(s), or root-relative (the local admin) — and never a character
  // that could close url("…").
  const SAFE_URL = /^(?:https?:\/\/|\/(?!\/))[^\s"'()\\<>]+$/;
  // R2 URLs are the filename unencoded ("Screenshot … 10.15.03 AM.png"), so the
  // characters SAFE_URL refuses are percent-encoded first and the ENCODED url is
  // tested and used. encodeURIComponent leaves ( ) ' alone, hence the fallback.
  const encodeUrlChar = (c) => {
    const e = encodeURIComponent(c);
    return e !== c ? e : '%' + c.charCodeAt(0).toString(16).toUpperCase().padStart(2, '0');
  };
  const safeImageUrl = (u) => {
    if (typeof u !== 'string') return null;
    const encoded = u.replace(/[\s"'()\\<>]/g, encodeUrlChar);
    return SAFE_URL.test(encoded) ? encoded : null;
  };
  const LIGHT_TEXT = '#f5f2ec';
  const DARK_TEXT = '#111421';
  const channel = (v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const luminance = (hex) => {
    const n = parseInt(hex.slice(1), 16);
    return 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
  };
  const contrastRatio = (a, b) => {
    const la = luminance(a), lb = luminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
  };
  // The text a custom color gets: the author's choice, or whichever reads better.
  const textToneFor = (colour, choice) => {
    if (choice === 'light' || choice === 'dark') return choice;
    return contrastRatio(colour, DARK_TEXT) > contrastRatio(colour, LIGHT_TEXT) ? 'dark' : 'light';
  };
  const clampInt = (v, lo, hi) => Math.min(hi, Math.max(lo, Math.round(v)));
  const isNum = (v) => typeof v === 'number' && isFinite(v);
  const settingsDecoration = (s) => {
    const out = { classes: [], style: {} };
    if (!s || typeof s !== 'object') return out;
    const img = s.bg === 'image' && s.bgImage && typeof s.bgImage === 'object' ? s.bgImage.url : null;
    const url = safeImageUrl(img);
    if (SETTINGS_BG_TOKENS.indexOf(s.bg) !== -1) {
      out.classes.push('blk--bg', 'blk--bg-' + s.bg);
    } else if (s.bg === 'custom' && typeof s.bgColor === 'string' && HEX_COLOUR.test(s.bgColor)) {
      const colour = s.bgColor.toLowerCase();
      out.classes.push('blk--bg', 'blk--bg-custom');
      if (textToneFor(colour, s.bgContrast) === 'dark') out.classes.push('blk--on-light');
      out.style['background-color'] = colour;
    } else if (url !== null) {
      const dark = s.bgContrast === 'dark';
      const pct = isNum(s.bgOverlay) ? clampInt(s.bgOverlay, 0, 100) : 70;
      const rgba = 'rgba(' + (dark ? '245, 242, 236' : '9, 13, 31') + ', ' + (pct / 100) + ')';
      out.classes.push('blk--bg', 'blk--bg-image');
      if (dark) out.classes.push('blk--on-light');
      out.style['background-image'] = 'linear-gradient(' + rgba + ', ' + rgba + '), url("' + url + '")';
    }
    if (isNum(s.padTop)) out.style['padding-top'] = clampInt(s.padTop, 0, 200) + 'px';
    if (isNum(s.padBottom)) out.style['padding-bottom'] = clampInt(s.padBottom, 0, 200) + 'px';
    if (s.width === 's') out.classes.push('blk--w-s');
    return out;
  };
  const applyBlockSettings = (node, settings) => {
    const d = settingsDecoration(settings);
    d.classes.forEach((c) => node.classList.add(c));
    Object.keys(d.style).forEach((k) => node.style.setProperty(k, d.style[k]));
  };

  // ---------- image zoom (slice 3, D4) ----------
  /* The image itself is the control (a wrapper would move the layout), and one
     native <dialog> shows the picture: made on first use and kept on <body>,
     so it outlives the lesson the classroom replaces under it. showModal()
     brings the focus trap and Escape; the `open` attribute is the fallback
     where it is missing. On close, focus goes back to the image that opened
     it (a no-op once the lesson under it was replaced: a removed node takes
     no focus). */
  let lightbox = null;
  let zoomedFrom = null;
  const closeLightbox = () => {
    if (!lightbox || !lightbox.hasAttribute('open')) return;
    if (typeof lightbox.close === 'function') lightbox.close();
    else lightbox.removeAttribute('open');
    const back = zoomedFrom;
    zoomedFrom = null;
    if (back) back.focus();
  };
  const openLightbox = (from) => {
    if (!lightbox || !lightbox.isConnected) {
      lightbox = el('dialog', 'lightbox');
      lightbox.setAttribute('aria-label', 'Enlarged image');
      const x = el('button', 'lightbox__close', '×');
      x.type = 'button';
      x.setAttribute('aria-label', 'Close');
      x.addEventListener('click', closeLightbox);
      lightbox.append(x, el('img', 'lightbox__img'));
      // Escape: the native cancel (closed by us, so focus is handled once),
      // and a keydown fallback for the attribute path.
      lightbox.addEventListener('cancel', (e) => { e.preventDefault(); closeLightbox(); });
      lightbox.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.preventDefault(); closeLightbox(); } });
      // A click outside the picture and the button lands on the dialog itself;
      // not the second click of a double-click, which would close what the first opened.
      lightbox.addEventListener('click', (e) => { if (e.target === lightbox && e.detail < 2) closeLightbox(); });
      document.body.appendChild(lightbox);
    }
    const big = lightbox.querySelector('.lightbox__img');
    big.src = from.getAttribute('src');
    big.alt = from.alt;
    zoomedFrom = from;
    if (typeof lightbox.showModal === 'function') lightbox.showModal();
    else lightbox.setAttribute('open', '');
    lightbox.querySelector('.lightbox__close').focus();
  };
  // A decorative image (alt '' on purpose; the serializer sends '' for nothing
  // else) stays out of the accessibility tree: mouse and touch zoom only.
  const zoomable = (picture, decorative) => {
    if (!decorative) {
      picture.setAttribute('role', 'button');
      picture.tabIndex = 0;
      picture.setAttribute('aria-haspopup', 'dialog');
      picture.setAttribute('aria-label', picture.alt ? 'Enlarge image: ' + picture.alt : 'Enlarge image');
      picture.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openLightbox(picture); }
      });
    }
    picture.setAttribute('data-zoom', '');
    picture.addEventListener('click', () => openLightbox(picture));
  };

  // ---------- opened items (slice 4b-1) ----------
  // Blocks made of items a student opens one by one, and the array holding
  // them. A Continue that waits for one of these waits until every item has
  // been opened; the first tab and the first step count as soon as they show.
  const ITEM_LISTS = { accordion: 'items', tabs: 'items', flashcards: 'cards', labeledGraphic: 'markers', process: 'steps' };
  // Slice 5 (D2, D6): the looks each renderer knows; absent or unknown = the first.
  const QUOTE_VARIANTS = ['a', 'b', 'c', 'd', 'onImage', 'carousel'];
  const GALLERY_VARIANTS = ['carousel', 'grid2', 'grid3', 'grid4'];
  const quoteVariant = (b) => (QUOTE_VARIANTS.includes(b.variant) ? b.variant : 'a');
  const galleryVariant = (b) => (GALLERY_VARIANTS.includes(b.variant) ? b.variant : 'carousel');
  // Slice 5 (D15): a carousel is opened slide by slide, like tabs.
  const itemListOf = (b) => {
    if (ITEM_LISTS[b.blockType]) return ITEM_LISTS[b.blockType];
    if (b.blockType === 'gallery' && galleryVariant(b) === 'carousel') return 'items';
    if (b.blockType === 'quote' && quoteVariant(b) === 'carousel') return 'items';
    return null;
  };
  const itemKey = (item, i) => (item && item.id) || String(i);
  const itemKeys = (b) => {
    const list = b[itemListOf(b)];
    return Array.isArray(list) ? list.map(itemKey) : [];
  };
  // Tell the lesson an item was opened: a no-op unless some Continue waits on the block.
  const opened = (b, ctx, item, i) => {
    if (b.id && ctx && typeof ctx.visit === 'function') ctx.visit(b.id, itemKey(item, i));
  };

  // ---------- block renderers ----------
  const R = {};

  // Slice 4b-3a (D1, Patrick: A): Rise's "Columns" text. The heading spans
  // both columns, a thin rule parts them, and phones stack them. Both columns
  // are always drawn, so an empty one keeps the layout. Absent = as before.
  R.text = (b) => {
    const columns = b.variant === 'columns';
    const wrap = el('div', columns ? 'blk blk-text blk-text--columns' : 'blk blk-text');
    if (b.heading) wrap.appendChild(el(b.level === 'h3' ? 'h3' : 'h2', 'blk-text__heading', b.heading));
    if (columns) {
      const cols = el('div', 'blk-text__cols');
      cols.append(rich('div', 'blk-prose', b.body), rich('div', 'blk-prose', b.bodyRight));
      wrap.appendChild(cols);
    } else if (b.body) {
      wrap.appendChild(rich('div', 'blk-prose', b.body));
    }
    return wrap;
  };

  // Slice 4b-2 (D1-D4): Rise's "Text on image" (overlay) and "Banner" layouts,
  // a corner radius for the picture (absent = today's 4px) and a link that
  // opens in a new tab instead of zooming. The overlay's text is asideBody.
  const IMAGE_LAYOUTS = ['hero', 'full', 'aside', 'overlay', 'banner'];
  // https only, and nothing that could leave the attribute: the twin of
  // Studio's HTTPS_LINK (admin src/lib/linkRules.ts); a test holds them together.
  const HTTPS_LINK = /^https:\/\/[^\s"'<>\\]+$/;
  R.image = (b) => {
    const layout = IMAGE_LAYOUTS.includes(b.layout) ? b.layout : 'full';
    // Side and size belong to the aside layout only (slice 3, D3). Absent or
    // unknown = today's image-left 5:7 grid; every other layout ignores both.
    let cls = 'blk blk-image blk-image--' + layout;
    if (layout === 'aside' && b.position === 'right') cls += ' blk-image--right';
    if (layout === 'aside' && (b.size === 's' || b.size === 'l')) cls += ' blk-image--size-' + b.size;
    const fig = el('figure', cls);
    if (isNum(b.cornerRadius)) fig.style.setProperty('--blk-radius', clampInt(b.cornerRadius, 0, 30) + 'px');
    const picture = img(b.image, 'blk-image__img');
    if (picture && layout === 'hero') picture.loading = 'eager';
    const link = layout !== 'overlay' && typeof b.link === 'string' && HTTPS_LINK.test(b.link) ? b.link : null;
    let shown = picture;
    if (picture && link) {
      const a = el('a', 'blk-image__link');
      a.href = link;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      // With no alt text the link would have no name: say what it does.
      if (!picture.alt) a.setAttribute('aria-label', 'Open link');
      a.appendChild(picture);
      shown = a;
    } else if (picture && layout !== 'overlay' && b.zoomOnClick !== false) {
      // Absent = on (slice 3, D1). img() flattens a null alt to '', so
      // decorative is read from the data. The overlay's photo is a backdrop.
      zoomable(picture, b.image.alt === '');
    }
    if (layout === 'aside') {
      const grid = el('div', 'blk-image__grid');
      if (shown) grid.appendChild(shown);
      grid.appendChild(rich('div', 'blk-prose blk-image__aside', b.asideBody));
      fig.appendChild(grid);
    } else if (layout === 'overlay') {
      const stage = el('div', 'blk-image__stage');
      if (picture) stage.appendChild(picture);
      stage.appendChild(rich('div', 'blk-prose blk-image__overlay', b.asideBody));
      fig.appendChild(stage);
    } else if (shown) {
      fig.appendChild(shown);
    }
    if (b.caption) fig.appendChild(el('figcaption', 'blk-image__caption', b.caption));
    return fig;
  };

  // Dividers are rendered by the segment engine (they need the reveal state).
  // Slice 4b-3a (D5, D6): a plain divider may instead be Rise's Numbered
  // divider (Patrick: A, the number in a ring on the line) or a Spacer.
  // `number` comes from numberDividers below. Absent or unknown = the line.
  const DIVIDER_VARIANTS = ['numbered', 'spacer'];
  R.divider = (b, state, ctx, number) => {
    const variant = !b.continue && DIVIDER_VARIANTS.includes(b.variant) ? b.variant : null;
    const wrap = el('div', 'blk blk-divider' + (b.continue ? ' blk-divider--continue' : variant ? ' blk-divider--' + variant : ''));
    if (b.continue) {
      const btn = el('button', 'btn btn--solid blk-divider__btn', b.label || 'Continue');
      btn.type = 'button';
      if (state.done) { btn.disabled = true; btn.classList.add('is-done'); btn.textContent = (b.label || 'Continue') + ' ✓'; }
      wrap.appendChild(btn);
      wrap._button = btn;
    } else if (variant === 'numbered') {
      wrap.setAttribute('role', 'separator');
      wrap.appendChild(el('span', 'blk-divider__num', number || 1));
    } else if (variant === 'spacer') {
      wrap.setAttribute('aria-hidden', 'true');
    } else {
      wrap.appendChild(el('hr', 'hairline'));
    }
    return wrap;
  };
  // Numbered dividers count 1, 2, 3… down the lesson, over numbered dividers
  // only; one with `restartAt` starts again there. Keyed by the block object.
  // Studio's canvas counts with a twin (admin
  // src/studio/blocks/divider/numbering.ts) that a test holds to this one.
  const numberDividers = (blocks) => {
    const out = new Map();
    let n = 0;
    blocks.forEach((b) => {
      if (!b || b.blockType !== 'divider' || b.continue || b.variant !== 'numbered') return;
      n = isNum(b.restartAt) ? clampInt(b.restartAt, 1, 9999) : n + 1;
      out.set(b, n);
    });
    return out;
  };

  // Slice 4b-3a (D3, D4): the titles' heading level (absent = today's h3;
  // .acc__h keeps the look, so only the outline a screen reader hears
  // changes) and "open one at a time": opening an item closes the open one.
  const ACC_LEVELS = ['h2', 'h3', 'h4', 'h5'];
  R.accordion = (b, state, ctx) => {
    const wrap = el('div', 'blk blk-accordion');
    const level = ACC_LEVELS.includes(b.headingLevel) ? b.headingLevel : 'h3';
    const setters = [];
    (b.items || []).forEach((item, i) => {
      const id = nextId('acc');
      const sect = el('div', 'acc');
      const h = el(level, 'acc__h');
      const btn = el('button', 'acc__btn');
      btn.type = 'button';
      btn.setAttribute('aria-expanded', 'false');
      btn.setAttribute('aria-controls', id + '-panel');
      btn.id = id + '-btn';
      btn.append(el('span', 'acc__title', item.title || 'Item ' + (i + 1)), el('span', 'acc__icon'));
      h.appendChild(btn);
      const panel = el('div', 'acc__panel');
      panel.id = id + '-panel';
      panel.setAttribute('role', 'region');
      panel.setAttribute('aria-labelledby', id + '-btn');
      panel.hidden = true;
      const p = img(item.image, 'acc__img');
      if (p) panel.appendChild(p);
      panel.appendChild(rich('div', 'blk-prose', item.body));
      const setOpen = (on) => {
        btn.setAttribute('aria-expanded', String(on));
        panel.hidden = !on;
        sect.classList.toggle('is-open', on);
      };
      setters.push(setOpen);
      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') === 'true';
        if (!open && b.oneAtATime === true) setters.forEach((set) => { if (set !== setOpen) set(false); });
        setOpen(!open);
        if (!open) opened(b, ctx, item, i);
      });
      sect.append(h, panel);
      wrap.appendChild(sect);
    });
    return wrap;
  };

  R.tabs = (b, state, ctx) => {
    const wrap = el('div', 'blk blk-tabs');
    const items = b.items || [];
    const list = el('div', 'tabs__list');
    list.setAttribute('role', 'tablist');
    const tabs = [];
    const panels = [];
    const activate = (idx, focus) => {
      tabs.forEach((t, i) => {
        const on = i === idx;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        t.classList.toggle('is-active', on);
        panels[i].hidden = !on;
      });
      opened(b, ctx, items[idx], idx);
      if (focus) tabs[idx].focus();
    };
    items.forEach((item, i) => {
      const id = nextId('tab');
      const t = el('button', 'tabs__tab', item.title || 'Tab ' + (i + 1));
      t.type = 'button';
      t.id = id;
      t.setAttribute('role', 'tab');
      t.setAttribute('aria-controls', id + '-panel');
      t.addEventListener('click', () => activate(i, false));
      t.addEventListener('keydown', (e) => {
        let n = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') n = (i + 1) % items.length;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = (i - 1 + items.length) % items.length;
        else if (e.key === 'Home') n = 0;
        else if (e.key === 'End') n = items.length - 1;
        if (n !== null) { e.preventDefault(); activate(n, true); }
      });
      const panel = el('div', 'tabs__panel');
      panel.id = id + '-panel';
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', id);
      panel.tabIndex = 0;
      const p = img(item.image, 'tabs__img');
      if (p) panel.appendChild(p);
      panel.appendChild(rich('div', 'blk-prose', item.body));
      tabs.push(t); panels.push(panel);
      list.appendChild(t);
    });
    wrap.appendChild(list);
    panels.forEach((p) => wrap.appendChild(p));
    if (tabs.length) activate(0, false);
    return wrap;
  };

  // Slice 4b-3b (D1-D4): Rise's Flashcard stack (Patrick: A, one card between
  // ‹ › arrows, then "2 of 5" over a progress line), the grid's card size, the
  // flip hint, and picture faces. Absent = today's grid of text cards.
  const FACE_IMAGES = ['centeredImage', 'fullImage'];
  // A face is text or a picture, never both (Rise): the picture only when the
  // type and a populated image are both there, else today's text face.
  const flashFace = (side, type, image, html) => {
    const cls = 'flash__face flash__face--' + side;
    if (!FACE_IMAGES.includes(type) || !image || !image.url) return rich('div', cls + ' blk-prose', html);
    const face = el('div', cls + ' flash__face--image' + (type === 'fullImage' ? ' flash__face--full' : ''));
    face.appendChild(img(image, 'flash__img'));
    return face;
  };
  R.flashcards = (b, state, ctx) => {
    const stack = b.variant === 'stack';
    let cls = 'blk blk-flash';
    if (stack) cls += ' blk-flash--stack';
    else if (b.size === 'small' || b.size === 'large') cls += ' blk-flash--' + b.size;
    const wrap = el('div', cls);
    const holder = el('div', stack ? 'flash__stage' : 'flash__grid');
    const cards = (b.cards || []).map((card, i) => {
      const btn = el('div', 'flash');
      btn.setAttribute('role', 'button');
      btn.tabIndex = 0;
      btn.setAttribute('aria-pressed', 'false');
      btn.setAttribute('aria-label', 'Flashcard ' + (i + 1) + ' — press to flip');
      const inner = el('div', 'flash__inner');
      const front = flashFace('front', card.frontType, card.frontImage, card.front);
      const back = flashFace('back', card.backType, card.backImage, card.back);
      back.setAttribute('aria-hidden', 'true');
      if (b.showFlipHint !== false) front.appendChild(el('span', 'flash__hint', 'Tap to flip'));
      inner.append(front, back);
      btn.appendChild(inner);
      const flip = () => {
        const flipped = btn.classList.toggle('is-flipped');
        btn.setAttribute('aria-pressed', String(flipped));
        front.setAttribute('aria-hidden', String(flipped));
        back.setAttribute('aria-hidden', String(!flipped));
        if (flipped) opened(b, ctx, card, i);
      };
      btn.addEventListener('click', flip);
      btn.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); flip(); } });
      holder.appendChild(btn);
      return btn;
    });
    wrap.appendChild(holder);
    if (stack && cards.length) {
      const arrow = (dir, label, glyph) => {
        const a = el('button', 'flash__arrow flash__arrow--' + dir, glyph);
        a.type = 'button';
        a.setAttribute('aria-label', label);
        return a;
      };
      const prev = arrow('prev', 'Previous card', '‹');
      const next = arrow('next', 'Next card', '›');
      holder.prepend(prev);
      holder.appendChild(next);
      const progress = el('div', 'flash__progress');
      const count = el('span', 'meta flash__count');
      const bar = el('span', 'flash__bar');
      const fill = el('span', 'flash__fill');
      bar.appendChild(fill);
      progress.append(count, bar);
      wrap.appendChild(progress);
      let idx = 0;
      const go = (i) => {
        idx = Math.max(0, Math.min(cards.length - 1, i));
        cards.forEach((c, j) => { c.hidden = j !== idx; });
        count.textContent = (idx + 1) + ' of ' + cards.length;
        fill.style.width = ((idx + 1) / cards.length) * 100 + '%';
        prev.disabled = idx === 0;
        next.disabled = idx === cards.length - 1;
      };
      // An arrow that just reached the end is disabled under the keyboard user's
      // focus; hand it to the card now showing (4b-3b review).
      const step = (d, arrowBtn) => { go(idx + d); if (arrowBtn.disabled) cards[idx].focus(); };
      prev.addEventListener('click', () => step(-1, prev));
      next.addEventListener('click', () => step(1, next));
      go(0);
    }
    return wrap;
  };

  /* Marker icons (slice 3, D6), drawn from this table in a 16 × 16 box.
     Strokes come from .lg__icon in base.css; a filled part says so itself.
     Studio's canvas draws from a twin (admin
     src/studio/blocks/labeledGraphic/markerIcons.ts) that a test deep-compares
     with this one. Absent or unknown = the marker's number, as before. */
  const deepFreeze = (o) => {
    Object.values(o).forEach((v) => { if (v && typeof v === 'object') deepFreeze(v); });
    return Object.freeze(o);
  };
  const FILLED = { fill: 'currentColor', stroke: 'none' };
  const MARKER_ICONS = deepFreeze({
    plus: [['path', { d: 'M8 3.5v9M3.5 8h9' }]],
    dot: [['circle', Object.assign({ cx: '8', cy: '8', r: '3' }, FILLED)]],
    info: [['path', { d: 'M8 7v5' }], ['circle', Object.assign({ cx: '8', cy: '4.25', r: '1.1' }, FILLED)]],
    question: [
      ['path', { d: 'M5.75 5.75a2.25 2.25 0 1 1 3.1 2.08c-.52.22-.85.72-.85 1.29v.63' }],
      ['circle', Object.assign({ cx: '8', cy: '12.25', r: '1.1' }, FILLED)],
    ],
    check: [['path', { d: 'M3.75 8.25l2.75 2.75 5.75-6' }]],
    pin: [
      ['path', { d: 'M8 14s4.25-4.1 4.25-7.25a4.25 4.25 0 0 0-8.5 0C3.75 9.9 8 14 8 14z' }],
      ['circle', { cx: '8', cy: '6.75', r: '1.5' }],
    ],
  });
  const SVG_NS = 'http://www.w3.org/2000/svg';
  // The glyph as an aria-hidden svg, or null (own keys only: 'toString' is no icon).
  const markerIcon = (name) => {
    if (!Object.prototype.hasOwnProperty.call(MARKER_ICONS, name)) return null;
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'lg__icon');
    svg.setAttribute('viewBox', '0 0 16 16');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    MARKER_ICONS[name].forEach(([tag, attrs]) => {
      const part = document.createElementNS(SVG_NS, tag);
      Object.keys(attrs).forEach((k) => part.setAttribute(k, attrs[k]));
      svg.appendChild(part);
    });
    return svg;
  };
  // Slice 4b-3b (D5, D6): the markers' colour (Patrick: A, the site's own;
  // absent = brass) and the picture's width (absent = the full column).
  const MARKER_COLORS = ['cream', 'navy', 'silver'];
  R.labeledGraphic = (b, state, ctx) => {
    let cls = 'blk blk-lg';
    if (MARKER_COLORS.includes(b.markerColor)) cls += ' blk-lg--marker-' + b.markerColor;
    if (b.imageWidth === 's' || b.imageWidth === 'm') cls += ' blk-lg--img-' + b.imageWidth;
    const wrap = el('div', cls);
    const stage = el('div', 'lg');
    const picture = img(b.image, 'lg__img');
    if (picture) { picture.loading = 'eager'; stage.appendChild(picture); }
    const pop = el('div', 'lg__pop');
    pop.setAttribute('role', 'dialog');
    pop.hidden = true;
    const popTitle = el('h4', 'lg__pop-title');
    popTitle.id = nextId('lgpop');
    pop.setAttribute('aria-labelledby', popTitle.id);
    const popBody = el('div', 'blk-prose lg__pop-body');
    const close = el('button', 'lg__close', '×');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    pop.append(close, popTitle, popBody);
    let openMarker = null;
    const closePop = () => {
      pop.hidden = true;
      if (openMarker) { openMarker.setAttribute('aria-expanded', 'false'); openMarker.classList.remove('is-open'); }
      const prev = openMarker; openMarker = null;
      if (prev && document.activeElement === close) prev.focus();
    };
    const markers = b.markers || [];
    markers.forEach((m, i) => {
      const x = Math.min(100, Math.max(0, Number(m.x) || 0));
      const y = Math.min(100, Math.max(0, Number(m.y) || 0));
      const icon = markerIcon(m.icon);
      const mk = el('button', icon ? 'lg__marker lg__marker--icon' : 'lg__marker', icon ? null : String(i + 1));
      if (icon) mk.appendChild(icon);
      mk.type = 'button';
      mk.style.left = x + '%';
      mk.style.top = y + '%';
      mk.setAttribute('aria-expanded', 'false');
      mk.setAttribute('aria-label', 'Marker ' + (i + 1) + (m.title ? ': ' + m.title : ''));
      mk.addEventListener('click', () => {
        if (openMarker === mk) { closePop(); return; }
        if (openMarker) { openMarker.setAttribute('aria-expanded', 'false'); openMarker.classList.remove('is-open'); }
        openMarker = mk;
        mk.setAttribute('aria-expanded', 'true');
        mk.classList.add('is-open');
        popTitle.textContent = m.title || 'Marker ' + (i + 1);
        popBody.innerHTML = typeof m.body === 'string' ? m.body : '';
        // Slice 4b-3b (D7): the marker's own picture, above its title.
        const oldPic = pop.querySelector('.lg__pop-img');
        if (oldPic) oldPic.remove();
        const pic = img(m.image, 'lg__pop-img');
        if (pic) pop.insertBefore(pic, popTitle);
        pop.style.left = x + '%';
        pop.style.top = y + '%';
        pop.classList.toggle('lg__pop--left', x > 50);
        pop.classList.toggle('lg__pop--up', y > 60);
        pop.classList.toggle('lg__pop--down', y < 30);
        pop.hidden = false;
        opened(b, ctx, m, i);
        close.focus();
      });
      stage.appendChild(mk);
    });
    stage.appendChild(pop);
    close.addEventListener('click', closePop);
    stage.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !pop.hidden) { e.stopPropagation(); closePop(); } });
    const outside = (e) => {
      if (!stage.isConnected) { document.removeEventListener('click', outside); return; }
      if (!pop.hidden && !stage.contains(e.target)) closePop();
    };
    document.addEventListener('click', outside);
    wrap.appendChild(stage);
    if (markers.length) wrap.appendChild(el('p', 'meta lg__hint', 'Select a marker to learn more'));
    return wrap;
  };

  // Slice 4b-3b (D8, Patrick: A, like Rise): an introduction screen, with a
  // Start button, before the steps, and a summary screen after them; each
  // only when its group's `show` is true. Absent = today's steps, and no Start
  // button is made. The counter and dots stay the steps' own.
  const bookendOf = (x) => (x && typeof x === 'object' && x.show === true ? x : null);
  R.process = (b, state, ctx) => {
    const wrap = el('div', 'blk blk-process');
    const steps = b.steps || [];
    if (!steps.length) return wrap;
    const intro = bookendOf(b.intro);
    const summary = bookendOf(b.summary);
    const screens = [];
    if (intro) screens.push({ kind: 'intro', item: intro });
    steps.forEach((s, n) => screens.push({ kind: 'step', item: s, n }));
    if (summary) screens.push({ kind: 'summary', item: summary });
    const first = intro ? 1 : 0; // the screen that holds step 1
    const head = el('div', 'proc__head');
    const counter = el('span', 'meta proc__counter');
    head.appendChild(counter);
    const dots = el('div', 'proc__dots');
    const dotBtns = steps.map((s, i) => {
      const d = el('button', 'proc__dot');
      d.type = 'button';
      d.setAttribute('aria-label', 'Go to step ' + (i + 1));
      d.addEventListener('click', () => show(first + i));
      dots.appendChild(d);
      return d;
    });
    head.appendChild(dots);
    const body = el('div', 'proc__body');
    body.setAttribute('aria-live', 'polite');
    const nav = el('div', 'proc__nav');
    const prev = el('button', 'btn btn--ghost', '← Back');
    const next = el('button', 'btn btn--ghost', 'Next →');
    prev.type = 'button'; next.type = 'button';
    nav.append(prev, next);
    let start = null;
    if (intro) {
      start = el('button', 'btn btn--solid proc__start', 'Start');
      start.type = 'button';
      nav.appendChild(start);
    }
    let idx = 0;
    const show = (i) => {
      idx = Math.max(0, Math.min(screens.length - 1, i));
      const sc = screens[idx];
      const s = sc.item;
      if (sc.kind === 'step') opened(b, ctx, s, sc.n);
      body.textContent = '';
      const p = img(s.image, 'proc__img');
      if (p) body.appendChild(p);
      const title = sc.kind === 'step' ? (s.title || 'Step ' + (sc.n + 1)) : s.title;
      if (title) body.appendChild(el('h3', 'proc__title', title));
      body.appendChild(rich('div', 'blk-prose', s.body));
      counter.textContent = sc.kind === 'intro' ? 'Introduction'
        : sc.kind === 'summary' ? 'Summary'
        : 'Step ' + (sc.n + 1) + ' of ' + steps.length;
      dotBtns.forEach((d, j) => {
        const on = sc.kind === 'step' && j === sc.n;
        d.classList.toggle('is-active', on);
        d.setAttribute('aria-current', on ? 'step' : 'false');
      });
      prev.disabled = idx === 0;
      next.disabled = idx === screens.length - 1;
      if (start) {
        const onIntro = sc.kind === 'intro';
        start.hidden = !onIntro;
        prev.hidden = onIntro;
        next.hidden = onIntro;
      }
    };
    prev.addEventListener('click', () => { show(idx - 1); focusThenScroll(body, false); });
    next.addEventListener('click', () => { show(idx + 1); focusThenScroll(body, false); });
    if (start) start.addEventListener('click', () => { show(first); focusThenScroll(body, false); });
    wrap.append(head, body, nav);
    show(0);
    return wrap;
  };

  const YT_ID = /^[A-Za-z0-9_-]{6,20}$/;
  // Rise's "Allow forward seeking" turned off (slice 4b-2, D6): a student can
  // rewind and replay, but never jump past the furthest point already watched.
  const noSkippingAhead = (v) => {
    let furthest = 0;
    // A seek's own closing timeupdate (queued before `seeked`) is not
    // watching: counted, small allowed jumps would walk `furthest` forward.
    let jumping = false;
    v.addEventListener('timeupdate', () => {
      if (!v.seeking && !jumping && v.currentTime > furthest) furthest = v.currentTime;
    });
    v.addEventListener('seeking', () => {
      jumping = true;
      if (v.currentTime > furthest + 0.5) v.currentTime = furthest;
    });
    v.addEventListener('seeked', () => { jumping = false; });
  };
  // A CORS load gets its own cache key (slice 4b-2 review): the bucket sends
  // no `Vary: Origin` on plain requests, so a copy the browser cached without
  // CORS would be reused for the CORS request and fail its check. r2.dev
  // ignores the query string.
  const corsUrl = (u) => u + (u.indexOf('?') === -1 ? '?' : '&') + 'cors=1';
  R.video = (b) => {
    const wrap = el('figure', 'blk blk-video');
    const frame = el('div', 'blk-video__frame');
    if (b.source === 'youtube' && YT_ID.test(b.youtubeId || '')) {
      const f = el('iframe');
      f.src = 'https://www.youtube-nocookie.com/embed/' + b.youtubeId + '?rel=0&modestbranding=1';
      f.title = b.caption || 'Video';
      f.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
      f.allowFullscreen = true;
      f.loading = 'lazy';
      frame.appendChild(f);
    } else if (b.source === 'embed' && /^https:\/\//.test(b.embedUrl || '')) {
      const f = el('iframe');
      f.src = b.embedUrl;
      f.title = b.caption || 'Embedded video';
      f.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
      f.referrerPolicy = 'strict-origin-when-cross-origin';
      f.allowFullscreen = true;
      f.loading = 'lazy';
      frame.appendChild(f);
    } else if (b.source === 'upload' && b.file && b.file.url) {
      const v = el('video');
      v.controls = true;
      v.preload = 'metadata';
      // Slice 4b-2 (D7): one caption track, off until the student presses CC.
      // The .vtt lives on the media bucket, another origin, so the video is
      // fetched with CORS (set before src); the bucket allows the site (D8).
      const captioned = !!(b.captionsFile && b.captionsFile.url);
      if (captioned) {
        v.crossOrigin = 'anonymous';
        const track = el('track');
        track.kind = 'captions';
        track.label = b.captionsLabel || 'English';
        // The default language says which it is (iOS auto-enables captions by language).
        if (!b.captionsLabel) track.srclang = 'en';
        track.src = b.captionsFile.url;
        v.appendChild(track);
      }
      v.src = captioned ? corsUrl(b.file.url) : b.file.url;
      // Slice 3 (D7): an uploaded video's own still. YouTube and embeds draw theirs.
      if (b.poster && b.poster.url) v.poster = b.poster.url;
      if (b.allowSeeking === false) noSkippingAhead(v);
      frame.classList.add('blk-video__frame--native');
      frame.appendChild(v);
    } else {
      frame.appendChild(el('p', 'auth__note blk-video__missing', 'This video is not available.'));
    }
    wrap.appendChild(frame);
    if (b.caption) wrap.appendChild(el('figcaption', 'blk-caption', b.caption));
    return wrap;
  };

  R.audio = (b) => {
    const wrap = el('figure', 'blk blk-audio');
    if (b.file && b.file.url) {
      const a = el('audio');
      a.controls = true;
      a.preload = 'metadata';
      a.src = b.file.url;
      wrap.appendChild(a);
    } else {
      wrap.appendChild(el('p', 'auth__note', 'This audio clip is not available.'));
    }
    // Slice 4b-2 (D9, Patrick: collapsed): the transcript, sanitised at publish,
    // folded away until the student opens it. Before the caption, because a
    // figure keeps its caption first or last.
    if (b.transcript) {
      const t = el('details', 'blk-audio__transcript');
      t.append(el('summary', 'blk-audio__summary', 'Transcript'), rich('div', 'blk-prose', b.transcript));
      wrap.appendChild(t);
    }
    if (b.caption) wrap.appendChild(el('figcaption', 'blk-caption', b.caption));
    return wrap;
  };

  /* Twins of Studio's friendlyTypeName and formatBytes (admin
     src/lib/uploadPolicy.ts), so the card words a file the way Studio's media
     picker does; a test holds them to the same output over a table. */
  const friendlyTypeName = (filename) => {
    const parts = filename.toLowerCase().split('.');
    const ext = parts.length < 2 ? '' : parts[parts.length - 1];
    if (ext === 'key') return 'Keynote presentation';
    if (ext === 'pptx' || ext === 'ppt') return 'PowerPoint presentation';
    if (ext === 'pdf') return 'PDF';
    if (ext === 'vtt') return 'Captions';
    return ext ? ext.toUpperCase() : 'File';
  };
  const formatBytes = (bytes) => {
    if (bytes >= 1024 ** 3) return (bytes / 1024 ** 3).toFixed(1) + ' GB';
    if (bytes >= 1024 ** 2) return Math.round(bytes / 1024 ** 2) + ' MB';
    if (bytes >= 1024) return Math.round(bytes / 1024) + ' KB';
    return bytes + ' bytes';
  };
  // The card's second line, "PDF · 149 MB" (slice 3, D5), or null when the size
  // is unknown: every lesson published before the serializer recorded it.
  const attachmentMeta = (file) =>
    file && typeof file.filesize === 'number'
      ? friendlyTypeName(file.filename || '') + ' · ' + formatBytes(file.filesize)
      : null;
  R.attachment = (b) => {
    const wrap = el('div', 'blk blk-attach');
    const file = b.file || {};
    const a = el('a', 'attach');
    if (file.url) { a.href = file.url; a.setAttribute('download', file.filename || ''); a.target = '_blank'; a.rel = 'noopener'; }
    const icon = el('span', 'attach__icon', (file.mimeType || '').includes('pdf') ? 'PDF' : 'FILE');
    const text = el('span', 'attach__text');
    text.appendChild(el('span', 'attach__label', b.label || file.filename || 'Download'));
    const meta = attachmentMeta(file);
    if (meta) text.appendChild(el('span', 'attach__meta', meta));
    else if (file.filename && file.filename !== b.label) text.appendChild(el('span', 'attach__name', file.filename));
    const cta = el('span', 'attach__cta', 'Download ↓');
    a.append(icon, text, cta);
    wrap.appendChild(a);
    return wrap;
  };

  // ---------- slice 5: eight more of Rise's blocks ----------
  // Twins of admin src/lib/linkRules.ts (a test holds them together).
  const EMAIL_ADDRESS = /^[^\s@<>"'\\]+@[^\s@<>"'\\]+\.[^\s@<>"'\\]+$/;
  const LESSON_HANDLE = /^[a-z0-9][a-z0-9-]{0,99}$/;

  // Slice 5 (D7): which pasted page URLs play in place. Twin: admin
  // src/studio/blocks/embed/embedPlayer.ts (a test holds them together).
  const YT_VIDEO = /^[A-Za-z0-9_-]{11}$/;
  const embedPlayerUrl = (url) => {
    let u;
    try { u = new URL(url); } catch (_) { return null; }
    if (u.protocol !== 'https:' || u.username || u.password || (u.port && u.port !== '443')) return null;
    const host = u.hostname.toLowerCase();
    const path = u.pathname;
    let id = null;
    if (host === 'youtu.be') {
      const m = path.match(/^\/([A-Za-z0-9_-]{11})\/?$/);
      id = m ? m[1] : null;
    } else if (['youtube.com', 'www.youtube.com', 'm.youtube.com', 'www.youtube-nocookie.com'].includes(host)) {
      if (path === '/watch') {
        const v = u.searchParams.get('v');
        id = v && YT_VIDEO.test(v) ? v : null;
      } else {
        const m = path.match(/^\/(?:embed|shorts|live)\/([A-Za-z0-9_-]{11})\/?$/);
        id = m ? m[1] : null;
      }
    }
    if (id) return 'https://www.youtube-nocookie.com/embed/' + id + '?rel=0&modestbranding=1';
    if (host === 'vimeo.com' || host === 'www.vimeo.com') {
      const m = path.match(/^\/(\d{6,12})(?:\/([0-9a-f]{6,20}))?\/?$/);
      if (m) return 'https://player.vimeo.com/video/' + m[1] + (m[2] ? '?h=' + m[2] : '');
    }
    if (host === 'player.vimeo.com') {
      const m = path.match(/^\/video\/(\d{6,12})\/?$/);
      const h = u.searchParams.get('h');
      if (m) return 'https://player.vimeo.com/video/' + m[1] + (h && /^[0-9a-f]{6,20}$/.test(h) ? '?h=' + h : '');
    }
    if (host === 'rumble.com' || host === 'www.rumble.com') {
      const m = path.match(/^\/embed\/([A-Za-z0-9]+)\/?$/);
      if (m) return 'https://rumble.com/embed/' + m[1] + '/';
    }
    return null;
  };

  // Slice 5 (D3, Patrick: A): the quote and gallery carousels, in the
  // flashcard stack's look — one slide between round ‹ › arrows, then "2 of
  // 5" over a progress line. Every slide is built and all but the current are
  // hidden. onShow(i) hears each slide shown, the first on arrival (D15).
  // Nothing is drawn for no slides. Studio's canvas draws the same markup
  // (admin src/studio/blocks/carousel/Carousel.tsx).
  const carousel = (wrap, slides, noun, onShow) => {
    if (!slides.length) return;
    const arrow = (dir, label, glyph) => {
      const a = el('button', 'carousel__arrow carousel__arrow--' + dir, glyph);
      a.type = 'button';
      a.setAttribute('aria-label', label);
      return a;
    };
    const prev = arrow('prev', 'Previous ' + noun, '‹');
    const next = arrow('next', 'Next ' + noun, '›');
    const stage = el('div', 'carousel__stage');
    stage.appendChild(prev);
    slides.forEach((s) => { s.tabIndex = -1; stage.appendChild(s); });
    stage.appendChild(next);
    const progress = el('div', 'carousel__progress');
    const count = el('span', 'meta carousel__count');
    const bar = el('span', 'carousel__bar');
    const fill = el('span', 'carousel__fill');
    bar.appendChild(fill);
    progress.append(count, bar);
    wrap.append(stage, progress);
    let idx = 0;
    const go = (i) => {
      idx = Math.max(0, Math.min(slides.length - 1, i));
      slides.forEach((s, j) => { s.hidden = j !== idx; });
      count.textContent = (idx + 1) + ' of ' + slides.length;
      fill.style.width = ((idx + 1) / slides.length) * 100 + '%';
      prev.disabled = idx === 0;
      next.disabled = idx === slides.length - 1;
      if (onShow) onShow(idx);
    };
    // An arrow that just reached the end is disabled under the keyboard
    // user's focus; hand it to the slide now showing (as the stack does).
    const step = (d, arrowBtn) => { go(idx + d); if (arrowBtn.disabled) slides[idx].focus(); };
    prev.addEventListener('click', () => step(-1, prev));
    next.addEventListener('click', () => step(1, next));
    go(0);
  };

  // Slice 5 (D1, Patrick: A, all five): Rise's Statement A-D share one DOM
  // and the CSS draws the difference; the Note is a callout with an "i".
  const STATEMENT_VARIANTS = ['a', 'b', 'c', 'd', 'note'];
  R.statement = (b) => {
    const variant = STATEMENT_VARIANTS.includes(b.variant) ? b.variant : 'a';
    const wrap = el('div', 'blk blk-statement blk-statement--' + variant);
    if (variant === 'note') {
      wrap.setAttribute('role', 'note');
      const icon = el('span', 'blk-statement__icon', 'i');
      icon.setAttribute('aria-hidden', 'true');
      wrap.appendChild(icon);
    }
    wrap.appendChild(rich('div', 'blk-prose blk-statement__body', b.body));
    return wrap;
  };

  // Slice 5 (D2, D3): Rise's Quote A-D, on an image, or a carousel. Every
  // look but the carousel shows the first quote only; the others stay in the
  // draft for when the author switches back.
  const quoteParts = (item) => {
    const parts = [];
    const photo = img(item && item.image, 'blk-quote__img');
    if (photo) parts.push(photo);
    const main = el('div', 'blk-quote__main');
    main.appendChild(rich('blockquote', 'blk-prose blk-quote__text', item && item.quote));
    if (item && typeof item.name === 'string' && item.name.trim()) main.appendChild(el('p', 'blk-quote__name', item.name));
    parts.push(main);
    return parts;
  };
  R.quote = (b, state, ctx) => {
    const variant = quoteVariant(b);
    const fig = el('figure', 'blk blk-quote blk-quote--' + variant);
    const items = Array.isArray(b.items) ? b.items : [];
    if (variant === 'carousel') {
      const slides = items.map((item) => {
        const slide = el('div', 'carousel__slide blk-quote__slide');
        slide.append(...quoteParts(item));
        return slide;
      });
      carousel(fig, slides, 'quote', (i) => opened(b, ctx, items[i], i));
    } else if (variant === 'onImage') {
      const stage = el('div', 'blk-quote__stage');
      const bg = img(b.bgImage, 'blk-quote__bg');
      if (bg) {
        bg.alt = '';
        bg.setAttribute('aria-hidden', 'true');
        stage.appendChild(bg);
      }
      const mark = el('span', 'blk-quote__mark', '“');
      mark.setAttribute('aria-hidden', 'true');
      stage.appendChild(mark);
      if (items.length) stage.append(...quoteParts(items[0]));
      fig.appendChild(stage);
    } else if (items.length) {
      fig.append(...quoteParts(items[0]));
    }
    return fig;
  };

  // Slice 5 (D4, Patrick: A): numbers are the item's place, so they follow a
  // reorder. A checkbox list saves the student's ticks (never graded, never
  // holding a Continue): the whole list per save, chained, so the last save
  // to land is the newest (the Worker merges one level deep).
  const LIST_VARIANTS = ['numbered', 'checkbox', 'bulleted'];
  R.list = (b, state, ctx) => {
    const variant = LIST_VARIANTS.includes(b.variant) ? b.variant : 'numbered';
    const wrap = el('div', 'blk blk-list blk-list--' + variant);
    const list = el(variant === 'numbered' ? 'ol' : 'ul', 'blk-list__items');
    const items = Array.isArray(b.items) ? b.items : [];
    const keys = items.map(itemKey);
    // Ticks for items since deleted are dropped, so they are never saved back.
    const ticked = new Set((state && Array.isArray(state.checked) ? state.checked : []).filter((k) => keys.indexOf(k) !== -1));
    let saving = Promise.resolve();
    items.forEach((item, i) => {
      const li = el('li', 'blk-list__item');
      const body = rich('div', 'blk-prose blk-list__body', item && item.body);
      if (variant === 'numbered') {
        const num = el('span', 'blk-list__num', i + 1);
        num.setAttribute('aria-hidden', 'true');
        li.appendChild(num);
      } else if (variant === 'bulleted') {
        const dot = el('span', 'blk-list__dot');
        dot.setAttribute('aria-hidden', 'true');
        li.appendChild(dot);
      } else {
        const box = el('input', 'blk-list__box');
        box.type = 'checkbox';
        body.id = nextId('list');
        box.setAttribute('aria-labelledby', body.id);
        box.checked = ticked.has(keys[i]);
        box.addEventListener('change', () => {
          if (box.checked) ticked.add(keys[i]);
          else ticked.delete(keys[i]);
          if (!b.id) return;
          const checked = Array.from(ticked);
          saving = saving.then(() => postBlockState(ctx, { [b.id]: { checked } }));
        });
        li.appendChild(box);
      }
      li.appendChild(body);
      list.appendChild(li);
    });
    wrap.appendChild(list);
    return wrap;
  };

  // Slice 5 (D5): Rise's Code snippet. Plain text, drawn as text; Copy uses
  // the Clipboard API and, where it is missing or refused, selects the code
  // for the student's own copy.
  R.code = (b) => {
    const fig = el('figure', 'blk blk-code');
    const panel = el('div', 'blk-code__panel');
    const copy = el('button', 'blk-code__copy', 'Copy');
    copy.type = 'button';
    copy.setAttribute('aria-label', 'Copy code');
    const pre = el('pre', 'blk-code__pre');
    const code = el('code', 'blk-code__code', b.code || '');
    pre.appendChild(code);
    panel.append(copy, pre);
    fig.appendChild(panel);
    if (b.caption) fig.appendChild(el('figcaption', 'blk-caption', b.caption));
    let timer = null;
    const copied = () => {
      copy.textContent = 'Copied';
      clearTimeout(timer);
      timer = setTimeout(() => { copy.textContent = 'Copy'; }, 2000);
    };
    const select = () => {
      const sel = window.getSelection && window.getSelection();
      if (!sel) return;
      const range = document.createRange();
      range.selectNodeContents(code);
      sel.removeAllRanges();
      sel.addRange(range);
    };
    copy.addEventListener('click', () => {
      const clip = navigator.clipboard;
      if (clip && typeof clip.writeText === 'function') clip.writeText(code.textContent).then(copied, select);
      else select();
    });
    return fig;
  };

  // Slice 5 (D6, D3): Rise's Gallery, a carousel or a grid of 2-4 columns,
  // with the image block's corner radius and zoom.
  R.gallery = (b, state, ctx) => {
    const variant = galleryVariant(b);
    const wrap = el('div', 'blk blk-gallery blk-gallery--' + variant);
    if (isNum(b.cornerRadius)) wrap.style.setProperty('--blk-radius', clampInt(b.cornerRadius, 0, 30) + 'px');
    const items = Array.isArray(b.items) ? b.items : [];
    const item = (it, extra) => {
      const fig = el('figure', 'blk-gallery__item' + extra);
      const picture = img(it && it.image, 'blk-gallery__img');
      if (picture) {
        // Absent = on; decorative is read from the data, as R.image does.
        if (b.zoomOnClick !== false) zoomable(picture, it.image.alt === '');
        fig.appendChild(picture);
      }
      if (it && it.caption) fig.appendChild(el('figcaption', 'blk-gallery__caption', it.caption));
      return fig;
    };
    if (variant === 'carousel') {
      carousel(wrap, items.map((it) => item(it, ' carousel__slide')), 'image', (i) => opened(b, ctx, items[i], i));
    } else {
      const grid = el('div', 'blk-gallery__grid');
      items.forEach((it) => grid.appendChild(item(it, '')));
      wrap.appendChild(grid);
    }
    return wrap;
  };

  // Slice 5 (D7): Rise's Embed. A video page plays in place (embedPlayerUrl);
  // any other https page is a link card from the details Studio read. Never
  // raw iframe HTML. Every detail is text.
  R.embed = (b) => {
    const fig = el('figure', 'blk blk-embed');
    const url = typeof b.url === 'string' ? b.url : '';
    const meta = b.meta && typeof b.meta === 'object' ? b.meta : {};
    const player = embedPlayerUrl(url);
    let host = null;
    if (!player && HTTPS_LINK.test(url)) {
      try { host = new URL(url).hostname; } catch (_) { host = null; }
    }
    if (player) {
      const frame = el('div', 'blk-video__frame');
      const f = el('iframe');
      f.src = player;
      f.title = meta.title || 'Embedded video';
      f.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
      f.allowFullscreen = true;
      f.setAttribute('loading', 'lazy');
      f.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
      frame.appendChild(f);
      fig.appendChild(frame);
      if (b.showMeta !== false && (meta.title || meta.description)) {
        const box = el('div', 'blk-embed__meta');
        if (meta.provider) box.appendChild(el('p', 'meta blk-embed__provider', meta.provider));
        if (meta.title) box.appendChild(el('p', 'blk-embed__title', meta.title));
        if (meta.description) box.appendChild(el('p', 'blk-embed__desc', meta.description));
        fig.appendChild(box);
      }
    } else if (host) {
      const a = el('a', 'blk-embed__card');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      // The third party's picture, sent no referrer.
      if (typeof meta.thumbnailUrl === 'string' && HTTPS_LINK.test(meta.thumbnailUrl)) {
        const thumb = el('img', 'blk-embed__thumb');
        thumb.src = meta.thumbnailUrl;
        thumb.alt = '';
        thumb.setAttribute('loading', 'lazy');
        thumb.setAttribute('referrerpolicy', 'no-referrer');
        a.appendChild(thumb);
      }
      const text = el('span', 'blk-embed__text');
      if (meta.provider) text.appendChild(el('span', 'meta blk-embed__provider', meta.provider));
      text.appendChild(el('span', 'blk-embed__title', meta.title || host));
      if (meta.description) text.appendChild(el('span', 'blk-embed__desc', meta.description));
      text.appendChild(el('span', 'blk-embed__cta', 'Open link ›'));
      a.appendChild(text);
      fig.appendChild(a);
    } else {
      fig.appendChild(el('p', 'auth__note', 'This embed is not available.'));
    }
    return fig;
  };

  // Slice 5 (D9, Patrick: A): Rise's Timeline, a line on the left with a
  // brass dot per event; free-text dates. A picture zooms (a decorative one
  // by mouse only, as R.image).
  R.timeline = (b) => {
    const wrap = el('div', 'blk blk-timeline');
    const list = el('ol', 'timeline');
    (Array.isArray(b.events) ? b.events : []).forEach((ev) => {
      const li = el('li', 'timeline__event');
      const dot = el('span', 'timeline__dot');
      dot.setAttribute('aria-hidden', 'true');
      li.appendChild(dot);
      if (ev && ev.date) li.appendChild(el('p', 'meta timeline__date', ev.date));
      if (ev && ev.title) li.appendChild(el('h3', 'timeline__title', ev.title));
      if (ev && typeof ev.body === 'string' && ev.body) li.appendChild(rich('div', 'blk-prose timeline__body', ev.body));
      const picture = img(ev && ev.image, 'timeline__img');
      if (picture) {
        zoomable(picture, ev.image.alt === '');
        li.appendChild(picture);
      }
      list.appendChild(li);
    });
    wrap.appendChild(list);
    return wrap;
  };

  // Slice 5 (D10, Patrick: A, solid brass): Rise's Button and Button stack.
  // A web page opens in a new tab, an email as mailto:, a lesson of this
  // course as #lesson=<handle> (player.js opens it, D11). A destination that
  // fails its rule draws a disabled button with no href.
  const BUTTON_ALIGNS = ['center', 'right'];
  R.buttons = (b) => {
    const wrap = el('div', 'blk blk-buttons' + (BUTTON_ALIGNS.includes(b.align) ? ' blk-buttons--' + b.align : ''));
    (Array.isArray(b.items) ? b.items : []).forEach((it) => {
      const item = el('div', 'blk-buttons__item');
      if (b.showDescription !== false && typeof it.description === 'string' && it.description) {
        item.appendChild(rich('div', 'blk-prose blk-buttons__desc', it.description));
      }
      const a = el('a', 'btn btn--solid blk-buttons__btn', it.label || 'Open');
      const value = typeof it.value === 'string' ? it.value : '';
      if (it.destType === 'url' && HTTPS_LINK.test(value)) {
        a.href = value;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
      } else if (it.destType === 'email' && EMAIL_ADDRESS.test(value)) {
        a.href = 'mailto:' + value;
      } else if (it.destType === 'lesson' && LESSON_HANDLE.test(value)) {
        a.href = '#lesson=' + encodeURIComponent(value);
      } else {
        a.setAttribute('aria-disabled', 'true');
        a.classList.add('is-disabled');
      }
      item.appendChild(a);
      wrap.appendChild(item);
    });
    return wrap;
  };

  // ---- knowledge check ----
  // Answer controls shared by knowledge checks and quiz questions, so both
  // draw the same markup.
  const legendFor = (kind) => kind === 'mr' ? 'Select all that apply' : kind === 'fillin' ? 'Type your answer' : kind === 'matching' ? 'Match each item' : 'Select one';
  // A radio or checkbox inside its label; returns the input.
  const choiceRow = (a, type, name) => {
    const lab = el('label', 'kc__opt');
    const inp = el('input');
    inp.type = type; inp.name = name; inp.value = a.id;
    lab.append(inp, el('span', 'kc__opt-text', a.text));
    return inp;
  };
  const fillInput = (name) => {
    const inp = el('input', 'field kc__fill');
    inp.type = 'text'; inp.name = name; inp.autocomplete = 'off';
    inp.setAttribute('aria-label', 'Your answer');
    return inp;
  };
  // One matching row: the item, and a select over the served options; returns the select.
  const matchRow = (a, name, opts) => {
    const row = el('div', 'kc__match');
    const lab = el('label', 'kc__match-left', a.text);
    const sel = el('select', 'field kc__select');
    sel.name = name + '-' + a.id; sel.dataset.answerId = a.id;
    lab.htmlFor = sel.id = nextId('sel');
    const ph = el('option', null, 'Choose…'); ph.value = '';
    sel.appendChild(ph);
    opts.forEach((o) => { const op = el('option', null, o); op.value = o; sel.appendChild(op); });
    row.append(lab, sel);
    return sel;
  };
  /* Attempts left on a check answered before this page load, or null for
     unlimited. Mirrors the Worker's kcRetries / kcAttempts / attemptsLeft
     (worker/src/lessons.ts): `retries` absent or invalid = unlimited, and a
     legacy { done: true } entry with no count is one attempt. */
  const kcAttemptsLeft = (b, state) => {
    const retries = Number.isInteger(b.retries) && b.retries >= 0 && b.retries <= 10 ? b.retries : -1;
    if (retries === -1) return null;
    const attempts = typeof state.attempts === 'number' ? state.attempts : (state.done ? 1 : 0);
    return Math.max(0, retries + 1 - attempts);
  };
  R.knowledgeCheck = (b, state, ctx) => {
    const wrap = el('div', 'blk blk-kc');
    wrap.appendChild(el('p', 'eyebrow', 'Knowledge check'));
    wrap.appendChild(rich('div', 'kc__q blk-prose', b.question));
    const picture = img(b.image, 'kc__img');
    if (picture) wrap.appendChild(picture);
    const form = el('form', 'kc__form');
    form.noValidate = true;
    const name = nextId('kc');
    const answers = b.answers || [];
    const kind = ['mc', 'mr', 'fillin', 'matching'].includes(b.kind) ? b.kind : 'mc';
    const inputs = [];
    const fieldset = el('fieldset', 'kc__fields');
    fieldset.appendChild(el('legend', 'kc__legend', legendFor(kind)));

    if (kind === 'mc' || kind === 'mr') {
      answers.forEach((a) => {
        const inp = choiceRow(a, kind === 'mc' ? 'radio' : 'checkbox', name);
        fieldset.appendChild(inp.parentNode); inputs.push(inp);
      });
    } else if (kind === 'fillin') {
      const inp = fillInput(name);
      fieldset.appendChild(inp); inputs.push(inp);
    } else {
      const opts = Array.isArray(b.options) ? b.options : [];
      answers.forEach((a) => {
        const sel = matchRow(a, name, opts);
        fieldset.appendChild(sel.parentNode); inputs.push(sel);
      });
    }
    form.appendChild(fieldset);

    const actions = el('div', 'kc__actions');
    const submit = el('button', 'btn btn--solid', 'Submit');
    submit.type = 'submit'; submit.disabled = true;
    const retry = el('button', 'btn btn--ghost', 'Try again');
    retry.type = 'button'; retry.hidden = true;
    actions.append(submit, retry);
    form.appendChild(actions);
    const result = el('div', 'kc__result');
    result.setAttribute('role', 'status'); result.setAttribute('aria-live', 'polite');
    result.hidden = true;
    form.appendChild(result);
    wrap.appendChild(form);

    const readAnswer = () => {
      if (kind === 'mc') { const c = inputs.find((i) => i.checked); return c ? c.value : null; }
      if (kind === 'mr') { const v = inputs.filter((i) => i.checked).map((i) => i.value); return v.length ? v : null; }
      if (kind === 'fillin') { const v = inputs[0].value.trim(); return v ? v : null; }
      const o = {};
      for (const s of inputs) { if (!s.value) return null; o[s.dataset.answerId] = s.value; }
      return o;
    };
    const lock = (locked) => inputs.forEach((i) => { i.disabled = locked; });
    // The answer key, returned after grading and only when the check reveals
    // it (D2). All of it is plain text, so it goes in through textContent.
    const showReveal = (reveal) => {
      if (!reveal || typeof reveal !== 'object') return;
      if ((kind === 'mc' || kind === 'mr') && Array.isArray(reveal.correctIds)) {
        inputs.forEach((inp) => {
          const lab = inp.parentNode;
          if (reveal.correctIds.indexOf(inp.value) !== -1) {
            lab.classList.add('is-answer');
            lab.appendChild(el('span', 'kc__sr', ' (correct answer)'));
          } else if (inp.checked) {
            lab.classList.add('is-wrong');
            lab.appendChild(el('span', 'kc__sr', ' (your answer, incorrect)'));
          }
        });
      } else if (kind === 'fillin' && Array.isArray(reveal.accepted) && reveal.accepted.length) {
        result.appendChild(el('p', 'kc__reveal', 'Accepted answers: ' + reveal.accepted.join(', ')));
      } else if (kind === 'matching' && reveal.matches && typeof reveal.matches === 'object') {
        inputs.forEach((sel) => {
          const match = reveal.matches[sel.dataset.answerId];
          if (typeof match !== 'string' || sel.value === match) return;
          sel.parentNode.classList.add('is-wrong');
          sel.parentNode.appendChild(el('p', 'kc__match-reveal', 'Correct: ' + match));
        });
      }
    };
    const clearReveal = () => {
      fieldset.querySelectorAll('.kc__sr, .kc__match-reveal').forEach((n) => n.remove());
      fieldset.querySelectorAll('.is-answer, .is-wrong').forEach((n) => n.classList.remove('is-answer', 'is-wrong'));
    };
    // The retry budget is spent (D3): no Try again, and the form stays locked.
    // The note only follows a miss: "Correct" needs no apology.
    const outOfAttempts = (note) => {
      lock(true); submit.hidden = true; retry.hidden = true;
      if (note) result.appendChild(el('p', 'auth__note kc__attempts', 'No attempts left.'));
      result.hidden = false;
    };
    const showResult = (correct, feedback, reveal) => {
      result.textContent = '';
      result.classList.toggle('is-correct', !!correct);
      result.classList.toggle('is-incorrect', !correct);
      result.appendChild(el('strong', 'kc__verdict', correct ? 'Correct' : 'Not quite'));
      if (typeof feedback === 'string' && feedback) result.appendChild(rich('div', 'blk-prose kc__feedback', feedback));
      showReveal(reveal);
      result.hidden = false;
      lock(true);
      submit.hidden = true;
      retry.hidden = false;
      wrap.classList.toggle('is-correct', !!correct);
      wrap.classList.toggle('is-incorrect', !correct);
    };
    const reset = () => {
      result.hidden = true; result.textContent = '';
      wrap.classList.remove('is-correct', 'is-incorrect');
      clearReveal();
      lock(false); submit.hidden = false; submit.disabled = true; retry.hidden = true;
      inputs.forEach((i) => { if (i.type === 'radio' || i.type === 'checkbox') i.checked = false; else i.value = ''; });
    };
    form.addEventListener('input', () => { submit.disabled = readAnswer() === null; });
    form.addEventListener('change', () => { submit.disabled = readAnswer() === null; });
    retry.addEventListener('click', reset);
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const answer = readAnswer();
      if (answer === null) return;
      submit.disabled = true; submit.textContent = 'Checking…';
      // No changed answer and no second submit while this one can still spend an attempt.
      lock(true);
      try {
        const r = await postJson(ctx, lessonPath(ctx, '/check'), { blockId: b.id, answer });
        if (r.status === 403 && r.json && r.json.error === 'no_retries_left') {
          result.textContent = '';
          result.classList.remove('is-correct', 'is-incorrect');
          outOfAttempts(true);
          focusThenScroll(result, false);
        } else {
          if (!r.ok || !r.json) throw new Error('check_failed');
          showResult(!!r.json.correct, r.json.feedback, r.json.reveal);
          if (r.json.attemptsLeft === 0) outOfAttempts(!r.json.correct);
          focusThenScroll(result, false);
          // `completed` is the Worker's: the lesson's completion after this answer.
          if (typeof ctx.onProgress === 'function') ctx.onProgress({ type: 'check', blockId: b.id, correct: !!r.json.correct, completed: r.json.completed === true });
        }
      } catch (_) {
        result.textContent = 'We couldn’t check that answer — please try again.';
        result.classList.remove('is-correct'); result.classList.add('is-incorrect');
        result.hidden = false; submit.disabled = false;
        lock(false);
      }
      submit.textContent = 'Submit';
    });

    // Previously answered: show the last recorded result.
    if (state && state.done) {
      showResult(state.correct !== false, null, null);
      result.appendChild(el('p', 'auth__note', 'You have already answered this question.'));
      if (kcAttemptsLeft(b, state) === 0) outOfAttempts(state.correct === false);
    }
    return wrap;
  };

  // A Continue that waits (slice 4b-1, Patrick: "like Rise"): an outlined bar
  // with a padlock and the hint stands in for the button until it opens.
  // Studio's canvas draws the same markup (admin src/studio/blocks/divider/Canvas.tsx).
  const LOCK_HINT = 'Complete the content above before moving on.';
  const lockBar = (text) => {
    const bar = el('p', 'blk-divider__lock');
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'blk-divider__lock-icon');
    svg.setAttribute('viewBox', '0 0 16 16');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    const body = document.createElementNS(SVG_NS, 'rect');
    body.setAttribute('x', '3'); body.setAttribute('y', '7');
    body.setAttribute('width', '10'); body.setAttribute('height', '7'); body.setAttribute('rx', '1.5');
    const shackle = document.createElementNS(SVG_NS, 'path');
    shackle.setAttribute('d', 'M5.5 7V5a2.5 2.5 0 0 1 5 0v2');
    svg.append(body, shackle);
    bar.append(svg, el('span', 'blk-divider__lock-text', text));
    return bar;
  };

  // ---------- content lesson: segments + continue dividers ----------
  const renderContent = (container, data, ctx) => {
    const content = data.content || {};
    const blocks = Array.isArray(content.blocks) ? content.blocks : [];
    const blockState = (data.block_state && typeof data.block_state === 'object') ? data.block_state : {};
    container.textContent = '';
    container.classList.add('blocks');

    if (!blocks.length) return { hasBlocks: false, continueTotal: 0 };
    const numbers = numberDividers(blocks); // slice 4b-3a D5

    // Split into segments at continue dividers; the divider closes its segment.
    const segments = [];
    let cur = { blocks: [], divider: null };
    blocks.forEach((b) => {
      cur.blocks.push(b);
      if (b.blockType === 'divider' && b.continue) { cur.divider = b; segments.push(cur); cur = { blocks: [], divider: null }; }
    });
    if (cur.blocks.length) segments.push(cur);

    const continueIds = segments.filter((s) => s.divider).map((s) => s.divider.id);
    const isDone = (id) => !!(blockState[id] && blockState[id].done === true);
    // "Require a correct answer" (D5): a Continue closing a segment that holds
    // an unsolved requireCorrect check stays locked, and the lesson is only
    // reported complete once every such check is solved (the Worker's rule).
    // Solved = right once, ever (D4's sticky `solved`), or a legacy entry that
    // was correct, or a correct answer on this page.
    const requiredIds = blocks
      .filter((b) => b.blockType === 'knowledgeCheck' && b.requireCorrect === true && b.id)
      .map((b) => b.id);
    const solvedNow = {};
    const isSolved = (id) => solvedNow[id] === true ||
      !!(blockState[id] && (blockState[id].solved === true || blockState[id].correct === true));
    const allRequiredSolved = () => requiredIds.every(isSolved);
    // Completion types (slice 4b-1): a Continue also waits for the block
    // directly above it (`blockAbove`) or for every block in its own segment
    // (`allAbove`, Rise's scoping). Complete = a check submitted (solved, for
    // requireCorrect), every item of an accordion/tabs/flashcards/labeled
    // graphic/process opened (slice 5: every slide of a carousel shown),
    // anything else once it shows.
    const submittedNow = {};
    const openedSets = {}; // blockId -> Set of item keys opened, on an earlier visit or this one
    const openedIn = (id) => openedSets[id] || (openedSets[id] = new Set(
      blockState[id] && Array.isArray(blockState[id].visited) ? blockState[id].visited : []));
    const isComplete = (b) => {
      if (b.blockType === 'knowledgeCheck') {
        if (b.requireCorrect === true) return isSolved(b.id);
        const s = blockState[b.id];
        return submittedNow[b.id] === true || !!(s && (typeof s.attempts === 'number' ? s.attempts > 0 : s.done === true));
      }
      if (!itemListOf(b)) return true;
      const seen = openedIn(b.id);
      return itemKeys(b).every((k) => seen.has(k));
    };
    // What each segment's Continue waits for: its requireCorrect checks (D5)
    // plus what its completion type names. Nothing once it has been clicked.
    const waits = segments.map((seg) => {
      const d = seg.divider;
      if (!d || isDone(d.id)) return [];
      const above = seg.blocks.slice(0, -1);
      const named = d.completion === 'allAbove' ? above : d.completion === 'blockAbove' ? above.slice(-1) : [];
      return above.filter((x) => named.indexOf(x) !== -1 || requiredIds.indexOf(x.id) !== -1);
    });
    const gates = []; // { button, lock, blocks }: a Continue and the blocks it waits for
    const refreshGates = () => gates.forEach((g) => {
      if (g.button.classList.contains('is-done')) return; // clicked: stays done
      const held = !g.blocks.every(isComplete);
      g.button.disabled = held;
      g.button.hidden = held;
      g.lock.hidden = !held;
    });
    // Only a block some Continue waits for saves what was opened, so a lesson
    // without completion types writes nothing new. The Worker merges one level
    // deep, so each save carries the whole list as it stood at that opening;
    // chained per block, so the last one to land is the fullest.
    const saveOpened = {};
    waits.forEach((list) => list.forEach((x) => { if (itemListOf(x) && x.id) saveOpened[x.id] = Promise.resolve(); }));
    const visit = (blockId, key) => {
      const seen = openedIn(blockId);
      if (seen.has(key)) return;
      seen.add(key);
      refreshGates();
      if (!saveOpened[blockId]) return;
      const list = Array.from(seen);
      saveOpened[blockId] = saveOpened[blockId].then(() =>
        postBlockState(ctx, { [blockId]: { visited: list } }));
    };
    // A lesson with no requireCorrect check and no Continue that waits gets the
    // caller's ctx, untouched. A requireCorrect check needs the wrapper even
    // with no Continue: its answer on this page counts toward completion (D5).
    const rctx = requiredIds.length === 0 && !waits.some((list) => list.length) ? ctx : Object.assign({}, ctx, {
      visit,
      onProgress: (evt) => {
        if (evt && evt.type === 'check') {
          submittedNow[evt.blockId] = true;
          if (evt.correct) solvedNow[evt.blockId] = true;
          refreshGates();
        }
        if (typeof ctx.onProgress === 'function') ctx.onProgress(evt);
      }
    });
    const segEls = [];
    let resumeTarget = null;

    const revealFrom = (startIdx) => {
      for (let i = startIdx; i < segEls.length; i++) {
        const s = segments[i];
        segEls[i].hidden = false;
        if (s.divider && !isDone(s.divider.id)) break;
      }
    };

    segments.forEach((seg, si) => {
      const segEl = el('section', 'blocks__segment');
      segEl.setAttribute('aria-label', 'Section ' + (si + 1));
      seg.blocks.forEach((b) => {
        const fn = R[b.blockType];
        if (!fn) { segEl.appendChild(el('div', 'blk blk-unknown auth__note', 'Unsupported block.')); return; }
        const st = blockState[b.id] || {};
        const node = fn(b, st, rctx, numbers.get(b));
        applyBlockSettings(node, b.settings);
        node.dataset.blockId = b.id || '';
        node.dataset.blockType = b.blockType;
        if (b.blockType === 'divider' && b.continue && node._button && !st.done) {
          if (waits[si].length) {
            const text = b.completion === 'blockAbove' || b.completion === 'allAbove'
              ? (b.hint || LOCK_HINT)
              : 'Answer the question above correctly to continue.';
            const lock = lockBar(text);
            node.appendChild(lock);
            gates.push({ button: node._button, lock, blocks: waits[si] });
          }
          const label = b.label || 'Continue';
          node._button.addEventListener('click', async () => {
            const btn = node._button;
            btn.disabled = true;
            btn.textContent = label + ' ✓';
            btn.classList.add('is-done');
            btn.classList.remove('is-unsaved');
            const firstReveal = !btn.dataset.revealed;
            btn.dataset.revealed = '1';
            blockState[b.id] = Object.assign({}, blockState[b.id], { done: true });
            revealFrom(si + 1);
            const nextSeg = segEls[si + 1];
            if (nextSeg && firstReveal) requestAnimationFrame(() => focusThenScroll(nextSeg));
            const allDone = continueIds.every(isDone);
            const r = await postBlockState(ctx, { [b.id]: { done: true } });
            if (!r.ok) {
              // Keep the reveal, but let the student retry the save. A failed beacon never counts as done.
              blockState[b.id].done = false;
              btn.disabled = false;
              btn.classList.remove('is-done');
              btn.classList.add('is-unsaved');
              btn.textContent = label + ' — not saved, try again';
              return;
            }
            // revealedAll and last tell the classroom where the lesson ends (Stage B2,
            // D14/D15): it shows its next-lesson bar, or opens the next lesson.
            if (typeof ctx.onProgress === 'function') ctx.onProgress({ type: 'continue', blockId: b.id, completed: allDone && allRequiredSolved(), revealedAll: allDone, last: b === blocks[blocks.length - 1] });
          });
        }
        segEl.appendChild(node);
      });
      segEl.hidden = true;
      segEls.push(segEl);
      container.appendChild(segEl);
    });
    revealFrom(0);
    refreshGates();

    // Resume: the first segment whose divider is not done, if any earlier divider was done.
    const anyDone = continueIds.some(isDone);
    if (anyDone) {
      const idx = segments.findIndex((s) => !s.divider || !isDone(s.divider.id));
      resumeTarget = segEls[idx === -1 ? segEls.length - 1 : idx];
    }

    // Read-to-the-end beacon — the Worker completes divider-free lessons on
    // this one. A lesson WITH continue dividers is completed by the dividers
    // themselves, so it needs no beacon at all.
    if (!data.completed && continueIds.length === 0 && segEls.length > 0) {
      whenReadToEnd(container, segEls[segEls.length - 1], () => {
        postBlockState(ctx, { __opened: true }).then((r) => {
          if (r.ok && typeof ctx.onProgress === 'function') ctx.onProgress({ type: 'opened', completed: allRequiredSolved() });
        });
      });
    }
    return { hasBlocks: true, continueTotal: continueIds.length, resumeTarget, revealedAll: continueIds.every(isDone) };
  };

  // ---------- quiz lesson ----------
  const renderQuiz = (container, data, ctx) => {
    const content = data.content || {};
    const allQuestions = Array.isArray(content.questions) ? content.questions : [];
    // This attempt's order: as served (the Worker randomizes it per student and
    // attempt when the quiz asks for that, D7), then the `questionOrder` a
    // grade returns for the next attempt.
    let questions = allQuestions;
    let nextOrder = null;
    // A quiz that draws from a question bank gets different questions every
    // attempt: the grade carries the next attempt's (`nextQuestions`, B4).
    let nextServed = null;
    const applyOrder = (ids) => {
      if (!Array.isArray(ids)) return;
      const ordered = ids.map((id) => allQuestions.filter((q) => q.id === id)[0]).filter(Boolean);
      if (ordered.length === allQuestions.length) questions = ordered;
    };
    const meta = Object.assign({ passingScore: content.passingScore || 70, retries: content.retries == null ? -1 : content.retries, attempts: 0, best: null, passed: false, timeLimitMinutes: content.timeLimitMinutes || null, lockNavigation: content.lockNavigation === true }, data.quiz || {});
    container.textContent = '';
    container.classList.add('blocks', 'blocks--quiz');
    const root = el('div', 'quiz');
    container.appendChild(root);
    const answers = {};
    const order = {}; // question id -> shuffled answer order, stable across Back/Next and retries
    const answerOrder = (q) => {
      if (!content.shuffleAnswers) return q.answers || [];
      if (!order[q.id]) order[q.id] = shuffle(q.answers || []);
      return order[q.id];
    };
    const retriesLeft = () => meta.retries === -1 ? Infinity : Math.max(0, (meta.retries + 1) - (meta.attempts || 0));
    const canAttempt = () => retriesLeft() > 0;
    const attemptsText = () => meta.retries === -1
      ? (meta.attempts || 0) + ' attempt' + (meta.attempts === 1 ? '' : 's') + ' used · unlimited retries'
      : (meta.attempts || 0) + ' of ' + (meta.retries + 1) + ' attempts used';

    // Time limit (Q3). `endsAt` is when this attempt's time runs out on the
    // LOCAL clock: the Worker's deadline minus its serverNow, from now, so a
    // wrong device clock does not matter. A reload resumes the same clock
    // because /quiz/start keeps the attempt's first start.
    let endsAt = null;
    let ticker = null;
    // The submit at 0:00 has its own one-shot timer: a hidden tab throttles the
    // 1 s interval (to once a minute after 5 min), which could push the submit
    // past the Worker's 30 s grace; a lone setTimeout is not throttled that way.
    let alarm = null;
    let onTimeUp = null; // set by question(): save the answer on screen, then submit
    const clockText = (ms) => {
      const s = Math.max(0, Math.ceil(ms / 1000));
      return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
    };
    const stopClock = () => {
      clearInterval(ticker); clearTimeout(alarm); ticker = alarm = null;
      document.removeEventListener('visibilitychange', tick);
    };
    const tick = () => {
      if (!root.isConnected) { stopClock(); return; }
      const left = endsAt - Date.now();
      const t = root.querySelector('.quiz__timer');
      if (t) t.textContent = clockText(left);
      if (left <= 0) { stopClock(); if (onTimeUp) onTimeUp(); }
    };
    const startClock = () => {
      stopClock();
      ticker = setInterval(tick, 1000);
      alarm = setTimeout(tick, endsAt - Date.now() + 50); // +50 ms: never a hair before 0:00
      document.addEventListener('visibilitychange', tick);
      tick();
    };
    const timeUp = () => endsAt !== null && Date.now() >= endsAt;

    // POST /quiz/start and set the attempt's clock from its answer; false when it failed.
    const startAttempt = async () => {
      let r;
      try { r = await postJson(ctx, lessonPath(ctx, '/quiz/start'), {}); }
      catch (_) { r = { ok: false, status: 0, json: null }; }
      if (!r.ok || !r.json) return false;
      endsAt = r.json.deadline ? Date.parse(r.json.deadline) - (Date.parse(r.json.serverNow) || Date.now()) + Date.now() : null;
      return true;
    };
    const START_ERROR = 'We couldn’t start the quiz — please try again.';

    // Start / Try again / Retake / Retry. A timed quiz asks the Worker to start
    // the attempt's clock first; an untimed one starts at once, as before.
    const begin = async (btn) => {
      Object.keys(answers).forEach((k) => delete answers[k]);
      stopClock();
      endsAt = null;
      meta.startedAt = meta.deadline = null; // the GET's running attempt is this one now: intro() must not offer it again
      if (meta.timeLimitMinutes) {
        btn.disabled = true;
        if (!(await startAttempt())) {
          btn.disabled = false;
          btn.parentNode.appendChild(el('p', 'form-error', START_ERROR));
          return;
        }
      }
      question(0);
      if (endsAt !== null) startClock();
    };

    const intro = () => {
      root.textContent = '';
      const card = el('div', 'quiz__card');
      card.appendChild(el('p', 'eyebrow', 'Quiz'));
      // A reload (or a second tab) mid-attempt: the GET carries the attempt's
      // start, and the time left is the Worker's deadline minus its serverNow,
      // never this device's clock. null: no attempt is running.
      const left = meta.deadline && meta.startedAt ? Date.parse(meta.deadline) - Date.parse(meta.serverNow) : null;
      card.appendChild(el('h2', 'quiz__title', left === null ? (meta.passed ? 'You passed this quiz' : 'Ready when you are') : (left > 0 ? 'Your attempt is in progress' : 'Time ran out')));
      const facts = el('dl', 'quiz__facts');
      const fact = (k, v) => { facts.appendChild(el('dt', 'meta', k)); facts.appendChild(el('dd', null, v)); };
      fact('Questions', String(questions.length));
      fact('Pass mark', meta.passingScore + '%');
      if (meta.timeLimitMinutes) fact('Time limit', meta.timeLimitMinutes + ' min');
      if (left > 0) fact('Time left', clockText(left));
      fact('Attempts', attemptsText());
      if (meta.best != null) fact('Best score', meta.best + '%');
      card.appendChild(facts);
      // Expired: the button runs the same begin(), whose clock is already at 0:00, so the existing time-up submit scores it.
      if (left !== null && !(left > 0)) card.appendChild(el('p', 'auth__note', 'Time ran out on your last attempt. It will be scored as 0.'));
      const btn = el('button', 'btn btn--solid', left === null ? (meta.passed ? 'Retake quiz' : (meta.attempts ? 'Try again' : 'Start quiz')) : (left > 0 ? 'Resume quiz' : 'Continue'));
      btn.type = 'button';
      if (!questions.length) { btn.disabled = true; card.appendChild(el('p', 'auth__note', 'This quiz has no questions yet.')); }
      else if (!canAttempt()) { btn.disabled = true; card.appendChild(el('p', 'auth__note', 'You have used all your attempts for this quiz.')); }
      btn.addEventListener('click', () => begin(btn));
      card.appendChild(btn);
      root.appendChild(card);
    };

    const question = (i) => {
      root.textContent = '';
      const q = questions[i];
      const card = el('div', 'quiz__card');
      const bar = el('div', 'quiz__bar');
      bar.setAttribute('role', 'progressbar');
      bar.setAttribute('aria-valuemin', '0'); bar.setAttribute('aria-valuemax', String(questions.length));
      bar.setAttribute('aria-valuenow', String(i + 1));
      bar.setAttribute('aria-label', 'Question ' + (i + 1) + ' of ' + questions.length);
      const fill = el('span', 'quiz__bar-fill');
      fill.style.width = Math.round(((i + 1) / questions.length) * 100) + '%';
      bar.appendChild(fill);
      card.appendChild(bar);
      const count = el('p', 'meta', 'Question ' + (i + 1) + ' of ' + questions.length);
      if (endsAt !== null) {
        // The countdown shares the count's row, under the progress bar.
        const head = el('div', 'quiz__nav quiz__head');
        const clock = el('span', 'meta quiz__timer', clockText(endsAt - Date.now()));
        clock.setAttribute('role', 'timer');
        clock.setAttribute('aria-label', 'Time left');
        head.append(count, clock);
        card.appendChild(head);
      } else card.appendChild(count);
      card.appendChild(rich('div', 'kc__q blk-prose', q.question));
      const form = el('form', 'kc__form'); form.noValidate = true;
      const fs = el('fieldset', 'kc__fields');
      const kind = ['mr', 'fillin', 'matching'].includes(q.kind) ? q.kind : 'mc';
      fs.appendChild(el('legend', 'kc__legend', legendFor(kind)));
      const name = nextId('qq');
      const prevAns = answers[q.id];
      // Answer shape per kind (D10): mc/mr string[] exactly as before, fill-in
      // a string, matching { [answerId]: string }.
      let read, answered;
      if (kind === 'fillin') {
        const inp = fillInput(name);
        if (typeof prevAns === 'string') inp.value = prevAns;
        fs.appendChild(inp);
        read = () => inp.value.trim();
        answered = () => read() !== '';
      } else if (kind === 'matching') {
        const opts = Array.isArray(q.options) ? q.options : [];
        const prev = prevAns && typeof prevAns === 'object' && !Array.isArray(prevAns) ? prevAns : {};
        const sels = (q.answers || []).map((a) => {
          const sel = matchRow(a, name, opts);
          if (typeof prev[a.id] === 'string') sel.value = prev[a.id];
          fs.appendChild(sel.parentNode);
          return sel;
        });
        // A half-finished match survives Back; Next waits for every row.
        read = () => {
          const o = {};
          sels.forEach((s) => { if (s.value) o[s.dataset.answerId] = s.value; });
          return o;
        };
        answered = () => sels.every((s) => s.value !== '');
      } else {
        const chosen = Array.isArray(prevAns) ? prevAns : [];
        const inputs = answerOrder(q).map((a) => {
          const inp = choiceRow(a, kind === 'mr' ? 'checkbox' : 'radio', name);
          inp.checked = chosen.includes(a.id);
          fs.appendChild(inp.parentNode);
          return inp;
        });
        read = () => inputs.filter((x) => x.checked).map((x) => x.value);
        answered = () => read().length > 0;
      }
      form.appendChild(fs);
      const nav = el('div', 'quiz__nav');
      const back = el('button', 'btn btn--ghost', '← Back'); back.type = 'button'; back.disabled = i === 0;
      const last = i === questions.length - 1;
      const next = el('button', 'btn btn--solid', last ? 'Submit answers' : 'Next →'); next.type = 'submit';
      const sync = () => { next.disabled = !answered(); };
      sync();
      form.addEventListener('change', sync);
      form.addEventListener('input', sync);
      back.addEventListener('click', () => { answers[q.id] = read(); question(i - 1); });
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const over = timeUp(); // only after a failed auto-submit: submit as it stands
        if (!answered() && !over) return;
        answers[q.id] = read();
        if (last || over) submitAll(next); else question(i + 1);
      });
      // At 0:00 the answer on screen is kept, answered or not, and everything is submitted.
      onTimeUp = () => { answers[q.id] = read(); submitAll(next); };
      // "No going back" (Q3): no Back button; Next already waits for an answer.
      if (!meta.lockNavigation) nav.appendChild(back);
      nav.appendChild(next);
      form.appendChild(nav);
      card.appendChild(form);
      root.appendChild(card);
      focusThenScroll(card);
    };

    const submitAll = async (btn, restarted) => {
      stopClock(); // no auto-submit while grading
      btn.disabled = true; btn.textContent = 'Grading…';
      // Shut the answers while grading: nothing can change them, so nothing
      // re-enables Submit mid-grade. Opened again only if grading fails.
      const fields = btn.form && btn.form.querySelector('fieldset');
      if (fields) fields.disabled = true;
      let r;
      try { r = await postJson(ctx, lessonPath(ctx, '/quiz'), { answers }); }
      catch (_) { r = { ok: false, status: 0, json: null }; }
      if (r.status === 403 && r.json && r.json.error === 'no_retries_left') {
        if (meta.retries >= 0) meta.attempts = meta.retries + 1;
        intro(); return;
      }
      let failed = 'We couldn’t grade the quiz — please try again.';
      if (r.status === 409 && r.json && r.json.error === 'not_started' && !restarted) {
        // The Worker holds no start for this attempt (the page is older than the
        // quiz's time limit, or another tab finished an attempt). The 409 proves
        // the quiz is timed, so start it whatever `meta` says, then send the same
        // answers once more.
        if (await startAttempt()) return submitAll(btn, true);
        failed = START_ERROR;
      }
      if (!r.ok || !r.json) {
        btn.disabled = false; btn.textContent = 'Submit answers';
        if (fields) fields.disabled = false;
        const err = el('p', 'form-error', failed);
        btn.parentNode.appendChild(err);
        // Time left: the clock runs on. Time up: the student submits by hand (no retry loop).
        if (endsAt !== null && !timeUp()) startClock();
        return;
      }
      const res = r.json;
      nextOrder = Array.isArray(res.questionOrder) ? res.questionOrder : null;
      nextServed = Array.isArray(res.nextQuestions) ? res.nextQuestions : null;
      meta.attempts = typeof res.attempts === 'number' ? res.attempts : (meta.attempts || 0) + 1;
      if (typeof res.best === 'number') meta.best = res.best;
      if (res.passed) meta.passed = true;
      result(res);
      if (typeof ctx.onProgress === 'function') ctx.onProgress({ type: 'quiz', completed: !!res.passed, score: res.score, passed: !!res.passed });
    };

    // One question's answer key from the grade (D2), as plain text.
    const revealFor = (q, reveal) => {
      if (q.kind === 'fillin') {
        return Array.isArray(reveal.accepted) && reveal.accepted.length
          ? el('p', 'quiz__reveal', 'Accepted answers: ' + reveal.accepted.join(', '))
          : null;
      }
      if (q.kind === 'matching') {
        const m = reveal.matches && typeof reveal.matches === 'object' ? reveal.matches : {};
        const rows = (q.answers || []).filter((a) => typeof m[a.id] === 'string');
        if (!rows.length) return null;
        const box = el('div', 'quiz__reveal');
        const list = el('ul', 'quiz__reveal-list');
        rows.forEach((a) => list.appendChild(el('li', null, a.text + ' → ' + m[a.id])));
        box.append(el('p', null, 'Correct matches:'), list);
        return box;
      }
      const ids = Array.isArray(reveal.correctIds) ? reveal.correctIds : [];
      const texts = (q.answers || []).filter((a) => ids.indexOf(a.id) !== -1).map((a) => a.text);
      return texts.length
        ? el('p', 'quiz__reveal', (texts.length > 1 ? 'Correct answers: ' : 'Correct answer: ') + texts.join(', '))
        : null;
    };

    const result = (res) => {
      root.textContent = '';
      const card = el('div', 'quiz__card quiz__result ' + (res.passed ? 'is-pass' : 'is-fail'));
      const score = Math.max(0, Math.min(100, Math.round(Number(res.score) || 0)));
      const ringWrap = el('div', 'quiz__ring');
      ringWrap.setAttribute('role', 'img');
      ringWrap.setAttribute('aria-label', 'Score ' + score + ' percent');
      const NS = 'http://www.w3.org/2000/svg';
      const svg = document.createElementNS(NS, 'svg');
      svg.setAttribute('viewBox', '0 0 120 120');
      const mk = (cls, dash) => {
        const c = document.createElementNS(NS, 'circle');
        c.setAttribute('cx', '60'); c.setAttribute('cy', '60'); c.setAttribute('r', '52');
        c.setAttribute('class', cls);
        if (dash != null) { c.setAttribute('stroke-dasharray', dash); }
        return c;
      };
      const circ = 2 * Math.PI * 52;
      svg.appendChild(mk('quiz__ring-track'));
      svg.appendChild(mk('quiz__ring-fill', (circ * score / 100).toFixed(1) + ' ' + circ.toFixed(1)));
      ringWrap.appendChild(svg);
      ringWrap.appendChild(el('span', 'quiz__ring-num', score + '%'));
      card.appendChild(ringWrap);
      if (res.timedOut) card.appendChild(el('p', 'auth__note quiz__timeout', 'Time ran out'));
      card.appendChild(el('p', 'eyebrow', res.passed ? 'Passed' : 'Not passed'));
      card.appendChild(el('h2', 'quiz__title', res.passed ? 'Well done.' : 'Keep going.'));
      card.appendChild(el('p', 'auth__note', 'Pass mark ' + meta.passingScore + '% · ' + attemptsText() + (meta.best != null ? ' · best ' + meta.best + '%' : '')));
      // perQuestion is empty when the quiz shows only the score (revealAnswers
      // 'none', D2): the ring and pass/fail are the whole result then.
      const per = Array.isArray(res.perQuestion) ? res.perQuestion : [];
      if (per.length) {
        // The grade lists questions as authored; number them as they were asked.
        const asked = (id) => { const k = questions.findIndex((x) => x.id === id); return k === -1 ? questions.length : k; };
        const list = el('ol', 'quiz__review');
        per.slice().sort((a, b) => asked(a.id) - asked(b.id)).forEach((p, i) => {
          const li = el('li', 'quiz__review-item ' + (p.correct ? 'is-correct' : 'is-incorrect'));
          const q = questions.find((x) => x.id === p.id);
          const head = el('div', 'quiz__review-head');
          head.appendChild(el('span', 'quiz__mark', p.correct ? '✓' : '✗'));
          head.appendChild(el('span', 'meta', 'Question ' + (i + 1)));
          li.appendChild(head);
          if (q) li.appendChild(rich('div', 'blk-prose quiz__review-q', q.question));
          if (typeof p.feedback === 'string' && p.feedback) li.appendChild(rich('div', 'blk-prose kc__feedback', p.feedback));
          const key = q && p.reveal && typeof p.reveal === 'object' ? revealFor(q, p.reveal) : null;
          if (key) li.appendChild(key);
          list.appendChild(li);
        });
        card.appendChild(list);
      }
      const actions = el('div', 'quiz__nav');
      if (canAttempt()) {
        const retry = el('button', 'btn btn--solid', res.passed ? 'Retake quiz' : 'Retry quiz');
        retry.type = 'button';
        retry.addEventListener('click', () => {
          if (nextServed) questions = nextServed; else applyOrder(nextOrder);
          begin(retry);
        });
        actions.appendChild(retry);
      } else if (!res.passed) {
        actions.appendChild(el('p', 'auth__note', 'You have used all your attempts for this quiz.'));
      }
      card.appendChild(actions);
      root.appendChild(card);
      focusThenScroll(card);
    };

    intro();
    return { hasBlocks: true, continueTotal: 0, resumeTarget: null };
  };

  window.DNA = window.DNA || {};
  window.DNA.blocks = {
    renderLesson(container, data, ctx) {
      if (!container) return { hasBlocks: false };
      return (data && data.kind === 'quiz') ? renderQuiz(container, data, ctx) : renderContent(container, data || {}, ctx);
    },
    renderers: R,
    // For tests: Studio's canvas runs an identical copy (admin src/lib/blockSettings.ts).
    settingsDecoration,
    // For tests and Studio's twins (slice 3, contract C4).
    attachmentMeta,
    MARKER_ICONS,
    // Slice 5 (D7): the twin of Studio's embedPlayer.ts.
    embedPlayerUrl
  };
})();
